import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { RoleForm, type RoleFormValues } from '@/components/roles/RoleForm';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { toast } from '@/lib/toast';
import { createRoleRequest } from '@/services/roles';
import type { CreateRoleBody, RoleDetail } from '@/types/roles';

export function RoleCreatePage(): JSX.Element {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const createMutation = useMutation<RoleDetail, ApiError, CreateRoleBody>({
    mutationFn: (body) => createRoleRequest(body),
    onSuccess: (role) => {
      toast.success(`Role '${role.name}' created`);
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      void navigate('/roles');
    },
    onError: (err) => {
      setErrorMessage(err.detail ?? err.message);
      toast.error('Could not create role', { description: err.detail ?? err.message });
    },
  });

  if (!permissions.includes('roles.create')) {
    return <Navigate to="/roles" replace />;
  }

  function handleSubmit(values: RoleFormValues): void {
    setErrorMessage(null);
    // Use mutate (not mutateAsync) so React Query's onError owns the rejection
    // path; this prevents unhandled-promise-rejection noise in tests.
    createMutation.mutate({
      name: values.name,
      description: values.description ?? null,
      permissionCodes: values.permissionCodes,
      isActive: values.isActive,
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Create Role</h1>
        <p className="text-muted-foreground">
          Define a new role with the permissions it should grant.
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>New role</CardTitle>
          <CardDescription>
            Pick a name (3–50 chars), an optional description, and at least one permission.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RoleForm
            mode="create"
            onSubmit={handleSubmit}
            submitting={createMutation.isPending}
            errorMessage={errorMessage}
          />
        </CardContent>
      </Card>
    </div>
  );
}
